import { observer } from 'mobx-react-lite'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from '@mui/material'
import { I18n, Translate } from 'common'
import { useSearchStore } from '../../../../store/search/SearchStore'
import { useUiStateStore } from '../../../../store/uiState/UiStateStore'
import { defaultTheme } from '../../../../theme/index'
import { breakpoints } from '../../../../theme/breakpoints'
import { zIndex } from '../../../../theme/zIndex'
import { Button } from '../../../../ui-components/forms/Button'
import BaseDrawer from '../../../../ui-components/modals/BaseDrawer'
import { CSVFileParser } from '../../../../ui-components/util/CSVFileParser'
import styled from 'styled-components'
import { MultiPartInquiry, MultiPartLinesData } from '../interfaces'

import DoneIcon from '@mui/icons-material/Done'
import WarningIcon from '@mui/icons-material/Warning'

export const MultiPartInquiryImportDrawer = observer((): ReactElement => {
  const {
    showMultiPartInquiryImportDrawer,
    setShowMultiPartInquiryImportDrawer,
    displaySuccessNotification,
  } = useUiStateStore()
  const { searchMultiPartInquiry } = useSearchStore()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.mobileLarge}px)`)
  const [importButtonDisabled, setImportButtonDisabled] = useState(true)
  const [showLinesData, setShowLinesData] = useState(false)
  const [importMultiPartsSearch, setImportMultiPartsSearch] = useState<
    Array<MultiPartInquiry>
  >([])
  const [multiPartsLinesData, setMultiPartsLinesData] =
    useState<MultiPartLinesData>({})

  const drawerBody = (
    <Container>
      <I18n name="importListDescription" />
      <a
        href="/csv/PARTINQUIRY_TEMPLATE.csv"
        style={{ marginTop: '15px', color: defaultTheme.colors.link }}
      >
        <I18n name="useTemplateCsv" />
      </a>
      {!showLinesData ? (
        <FileContainer>
          <CSVFileParser
            setImportButtonDisabled={setImportButtonDisabled}
            setImportMultiPartsSearch={setImportMultiPartsSearch}
            setMultiPartsLinesData={setMultiPartsLinesData}
          />
        </FileContainer>
      ) : (
        <LinesDataContainer>
          <DoneIcon style={{ color: defaultTheme.colors.green, scale: '2' }} />
          <I18n
            name={Translate('listImportedSuccessfully', [
              multiPartsLinesData?.successLines || '',
            ])}
            style={{
              fontSize: '16px',
              marginTop: '15px',
              fontWeight: 'bold',
            }}
          />
          {multiPartsLinesData.errorLines &&
          multiPartsLinesData.errorLines > 0 ? (
            <ErrorContainer>
              <WarningIcon
                style={{
                  color: defaultTheme.colors.red,
                  marginLeft: '10px',
                  marginRight: '10px',
                }}
              />
              <I18n
                name={Translate('listImportedError', [
                  multiPartsLinesData.errorLines,
                ])}
                style={{ fontSize: '16px' }}
              />
            </ErrorContainer>
          ) : null}
        </LinesDataContainer>
      )}
    </Container>
  )

  const drawerFooter = (
    <ButtonsWrapper>
      <Button
        text="cancel"
        onClick={() => {
          setShowMultiPartInquiryImportDrawer(false)
        }}
        variant="secondary"
      />

      {!showLinesData ? (
        <Button
          text="import"
          onClick={() => {
            if (importMultiPartsSearch?.length > 0) {
              setShowLinesData(true)
            }
          }}
          variant="primary"
          disabled={importButtonDisabled}
        />
      ) : (
        <Button
          text="finish"
          onClick={() => {
            searchMultiPartInquiry(importMultiPartsSearch)
            navigate('/searchResults')
            setShowMultiPartInquiryImportDrawer(false)
            displaySuccessNotification('listImported')
          }}
          variant="primary"
        />
      )}
    </ButtonsWrapper>
  )

  const resetFileSelection = useCallback(() => {
    setImportButtonDisabled(true)
    setImportMultiPartsSearch([])
    setMultiPartsLinesData({})
    setShowLinesData(false)
  }, [])

  useEffect(() => {
    if (showMultiPartInquiryImportDrawer) {
      resetFileSelection()
    }
  }, [resetFileSelection, showMultiPartInquiryImportDrawer])

  return (
    <BaseDrawer
      open={showMultiPartInquiryImportDrawer}
      title="importList"
      body={drawerBody}
      onClose={() => {
        setShowMultiPartInquiryImportDrawer(false)
      }}
      width={isMobile ? '100%' : '600px'}
      minWidth={isMobile ? '0' : '500px'}
      paddingX={isMobile ? '16px' : '2rem'}
      footer={drawerFooter}
    />
  )
})

const ButtonsWrapper = styled.div`
  justify-content: end;
  align-items: end;
  display: flex;
  gap: 12px;
  margin-top: 16px;

  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));

    button {
      width: 100%;
      min-width: 0;
    }
  }
`

const Container = styled.div`
  background-color: ${defaultTheme.colors.componentsBackground};
  z-index: ${zIndex.modal};
  display: flex;
  flex-direction: column;
`

const FileContainer = styled.div`
  position: relative;
  border-style: dashed;
  border-color: lightgrey;
  border-radius: 5px;
  margin-top: 15px;
  padding: 15px;
`

const LinesDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 15px;
  padding: 15px;
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: whitesmoke;
  border-radius: 5px;
  border: 1px solid silver;
  padding: 10px;
  margin-top: 15px;
`
